import type { ClientListItem } from "@planerius/contracts/cabinet"

import type { ApiProblem } from "../../../api/problem"

/**
 * Список клиентов 3-А1 — СЕМЬ СОСТОЯНИЙ.
 *
 * Здесь нет разметки, потому что состояние списка — это правило, а не вид. Ошибка,
 * которую невозможно заметить глазами на заполненном экране и которая стоит дороже всех
 * остальных: ПУСТО В ПЕРВЫЙ ДЕНЬ и ПУСТО ПО ФИЛЬТРУ — разные состояния с разными текстами
 * и разными действиями (ТЗ §5.6). Салон, которому в его первый день показали «Проверьте
 * номер — возможно, клиент записан под другим именем», решит, что программа сломана:
 * он ещё никого не искал.
 *
 * Офлайн СЮДА НЕ ВХОДИТ и это не упущение: в макете он идёт ПОВЕРХ списка полосой,
 * а не вместо него. Чтение работает всегда, изменения — только со связью (ТЗ §5.11),
 * поэтому офлайн гасит кнопки, а не содержимое.
 */

export interface ClientsFilters {
  readonly search: string
  /** «Не были больше 3 месяцев». */
  readonly staleOnly: boolean
  /** «Ограничение записи». */
  readonly blockedOnly: boolean
  /** «Неявки». */
  readonly noShowOnly: boolean
  /** «Мастер последнего визита». */
  readonly employeeId: string | null
}

export const EMPTY_FILTERS: ClientsFilters = {
  search: "",
  staleOnly: false,
  blockedOnly: false,
  noShowOnly: false,
  employeeId: null,
}

/** Сужен ли список хоть чем-нибудь. От этого зависит, какое «пусто» показывать. */
export function isNarrowed(filters: ClientsFilters): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.staleOnly ||
    filters.blockedOnly ||
    filters.noShowOnly ||
    filters.employeeId !== null
  )
}

export type ClientsListState =
  /** Нет права clients.list.view — мастер, пришедший по ссылке. */
  | { readonly kind: "denied" }
  /** Первая загрузка: каркас строк, а не крутящийся кружок. */
  | { readonly kind: "loading" }
  /** Не загрузилось и показывать нечего. */
  | { readonly kind: "problem"; readonly problem: ApiProblem }
  /** В базе никого и никто ничего не искал. */
  | { readonly kind: "empty_first_day" }
  /** Искали — не нашли. */
  | { readonly kind: "empty_filter"; readonly search: string }
  | {
      readonly kind: "list"
      readonly items: readonly ClientListItem[]
      /** Есть ли что подгружать дальше. */
      readonly hasMore: boolean
    }

export interface ClientsStateInput {
  readonly canView: boolean
  readonly loading: boolean
  readonly problem: ApiProblem | null
  readonly items: readonly ClientListItem[] | null
  readonly nextCursor: string | null
  readonly filters: ClientsFilters
}

export function clientsListState(input: ClientsStateInput): ClientsListState {
  // Право проверяется ПЕРВЫМ: показать каркас загрузки тому, кому раздел закрыт, —
  // значит пообещать данные, которых не будет.
  if (!input.canView) return { kind: "denied" }

  // Сбой поверх уже показанного списка списка не стирает: это отдельная полоса.
  if (input.problem !== null && input.items === null) {
    return { kind: "problem", problem: input.problem }
  }

  if (input.items === null || (input.loading && input.items.length === 0)) {
    return { kind: "loading" }
  }

  if (input.items.length === 0) {
    return isNarrowed(input.filters)
      ? { kind: "empty_filter", search: input.filters.search.trim() }
      : { kind: "empty_first_day" }
  }

  return { kind: "list", items: input.items, hasMore: input.nextCursor !== null }
}

/**
 * Счётчик в шапке.
 *
 * «Найдено 143 из 20 412» из макета собирается из ДВУХ чисел, а сервер отдаёт одно:
 * totalCount считается для текущего запроса. Второе — размер базы — берётся из последней
 * НЕсуженной загрузки того же экрана. Это честное число: оно пришло с сервера, просто
 * раньше. Пока его нет (экран открыли сразу с поиском), показывается только найденное —
 * выдумывать размер базы нельзя, по нему судят о переносе данных из прежней программы.
 */
export function clientsCountLabel(input: {
  readonly state: ClientsListState
  readonly totalCount: number | null
  readonly baseCount: number | null
  readonly narrowed: boolean
}): string {
  if (input.state.kind === "loading") return "загружаем…"
  if (input.state.kind === "empty_first_day" || input.state.kind === "denied") return ""
  if (input.state.kind === "problem") return ""

  if (input.narrowed) {
    if (input.totalCount === null) return ""
    const found = `Найдено ${formatCount(input.totalCount)}`
    return input.baseCount === null ? found : `${found} из ${formatCount(input.baseCount)}`
  }

  if (input.totalCount === null) return ""
  return `${formatCount(input.totalCount)} ${clientWord(input.totalCount)} в базе`
}

/** Подвал таблицы: «Показано 10 из 1 284 · подгружаем по мере прокрутки». */
export function clientsFooterLabel(input: {
  readonly shown: number
  readonly totalCount: number | null
  readonly hasMore: boolean
}): string {
  const head =
    input.totalCount === null
      ? `Показано ${formatCount(input.shown)}`
      : `Показано ${formatCount(input.shown)} из ${formatCount(input.totalCount)}`
  return input.hasMore ? `${head} · подгружаем по мере прокрутки` : head
}

/** Пробел между разрядами — неразрывный: «1 284» не должно переноситься по строке. */
export function formatCount(value: number): string {
  return value.toLocaleString("ru-RU").replaceAll(" ", " ")
}

function clientWord(count: number): string {
  const mod100 = count % 100
  if (mod100 >= 11 && mod100 <= 14) return "клиентов"
  const mod10 = count % 10
  if (mod10 === 1) return "клиент"
  if (mod10 >= 2 && mod10 <= 4) return "клиента"
  return "клиентов"
}

/**
 * Телефон в строке списка.
 *
 * Номер ГАСИТ СЕРВЕР: в ответе приходит phone: null и остаётся phoneMasked. Кабинет
 * не решает это заново и не прячет то, что уже приехало, — он показывает ровно то,
 * что ему дали. Пустая строка вместо номера была бы хуже маски: маска говорит
 * «номер есть, но закрыт», пустота — «номера нет», и администратор идёт его спрашивать.
 */
export function clientPhoneLabel(item: ClientListItem): { readonly text: string; readonly masked: boolean } {
  if (item.phone !== null && item.phone.length > 0) return { text: item.phone, masked: false }
  if (item.phoneMasked !== null && item.phoneMasked.length > 0) {
    return { text: item.phoneMasked, masked: true }
  }
  return { text: "—", masked: false }
}

/** Дата «не были больше 3 месяцев» от сегодняшнего дня филиала. Календарная, не «90 дней». */
export function threeMonthsBefore(today: string): string {
  const parsed = new Date(`${today}T12:00:00Z`)
  parsed.setUTCMonth(parsed.getUTCMonth() - 3)
  return parsed.toISOString().slice(0, 10)
}

// ───────────────────────── тексты пустых состояний ─────────────────────────

export const EMPTY_FIRST_DAY = {
  title: "В базе пока никого",
  text: "Клиенты появятся сами, когда вы начнёте записывать людей в журнал, — или перенесите базу из прежней программы.",
  note: "Файл на 3 000 строк из YCLIENTS или DIKIDI — 10 минут. Историю визитов конкуренты не отдают, об этом скажем до загрузки.",
} as const

export const EMPTY_FILTER = {
  text: "Проверьте номер — возможно, клиент записан под другим именем.",
} as const

export const DENIED = {
  title: "База клиентов салона доступна администратору и управляющему",
  text: "Ваша роль — мастер. Ваши клиенты — в вашем расписании.",
  action: "Открыть мой день",
} as const

/** Подпись выключенной кнопки выгрузки. Ровно та, что стоит в макете в title. */
export const EXPORT_DENIED_REASON = "Только владелец · второй фактор · квота 500 в сутки"

export const OFFLINE_STRIPE = {
  title: "Нет связи.",
  text: "Показан список на {time}, новые клиенты в нём могут отсутствовать. Создание клиента недоступно",
} as const
